/**
 * DEFENSIVE CONVERSATION FILTERING (HPWEB-6574 | HPWEB-6576)
 *
 * Context: Backend API occasionally returns conversations for the wrong listing instead
 * of only returning conversations for the requested listing (identified by aliasEncoded).
 *
 * Solution: Two-layer defensive filtering:
 * 1. ConversationActions: Filter conversations by listingAlias before storing in Redux
 * 2. Conversation model: Secondary validation to catch any remaining mismatches
 *
 * Impact: When backend returns wrong conversations, they are silently filtered out,
 * preventing UI bugs where "Contact again?" prompts appear for the wrong listings.
 *
 * Logging: Mismatches are logged with structured data for monitoring. DataDog can
 * be configured to alert if mismatch frequency increases significantly.
 */

import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import isEmpty from 'lodash/isEmpty';

import api from 'app/shared/utils/api';
import constants from 'app/shared/constants/ConstantsBundle';
import Conversation from 'app/shared/models/Conversation';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import type { ConversationData } from 'app/types';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ThunkResult } from 'app/types/redux.type';

const logger = getGlobalLogger('actions/conversation');

const ConversationActions = {
  fetchMessageCount(): ThunkResult<Promise<unknown>> {
    return async (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>, getState) => {
      const store = getState();
      const { user } = store;
      const { loggedIn } = user || {};

      if (!loggedIn) {
        return Promise.resolve();
      }

      const apiResponse = await dispatch(api.listing.conversation.messageCounts());
      const { data = {} } = apiResponse;
      const { unreadCount } = data;

      return dispatch({
        type: constants.UPDATE_UNREAD_CONVERSATION_COUNT,
        payload: unreadCount,
      });
    };
  },

  /**
   * Optimistically add sent message details for Rachel Satellite to user.conversations reducer
   * so we can immediately display feedback that shows the user sent a message,
   * rather than waiting for Conversation Service to parse / update message, which can take
   * a few seconds.
   */
  optimisticSetSentMessage(aliasEncoded: string): ThunkResult | Promise<void> {
    if (!aliasEncoded) {
      return Promise.resolve();
    }

    return (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => {
      const conversation = {
        lastSender: 'user',
        id: '',
        lastMessageTimestamp: Date.now(),
        isUnread: false,
        satelliteEnabled: true,
      };

      // Call conversation service after a successful submit in order to update
      // HDP with correct conversation link id + url. Because recent messages aren't immediately
      // available, we first optimistically set a sent message status to display on HDP,
      // and then wait a few seconds before calling the backend API.
      setTimeout(() => {
        dispatch(ConversationActions.fetchRecentMessages({ aliasEncoded }));
      }, 5000);

      return dispatch({
        type: constants.UPDATE_LISTING_CONVERSATION,
        payload: {
          aliasEncoded,
          conversation,
        },
      });
    };
  },

  fetchRecentMessages({ aliasEncoded }: { aliasEncoded: string | null }): ThunkResult<Promise<unknown>> {
    return async (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>, getState) => {
      const store = getState();
      const { user } = store;
      const { loggedIn } = user || {};

      if (!loggedIn || !aliasEncoded) {
        return Promise.resolve();
      }

      // If conversation exists, the API will return a response with a data object.
      // If no conversation exists between the user and tenant, no data will be
      // returned from the API.
      const apiResponse = await dispatch(api.listing.conversation.recentMessages(aliasEncoded));
      const { data = {} } = apiResponse;
      const { conversations = [] } = data;

      if (!isEmpty(conversations)) {
        // DEFENSIVE FILTERING: The API should only return conversations for the requested listing.
        // However, a backend bug may return conversations for other listings.
        // Filter to only conversations matching the requested aliasEncoded.
        const matchingConversations = conversations.filter((conv: ConversationData) => {
          const conversationListingAlias = conv?.listingAlias;
          if (conversationListingAlias === aliasEncoded) {
            return true;
          }

          // Log the mismatch for monitoring
          if (conversationListingAlias) {
            logger?.warn(
              `Defensive filtering: Conversation returned with mismatched listing. ` +
                `Requested: ${aliasEncoded}, Received: ${conversationListingAlias}, ` +
                `ConversationId: ${conv?.conversationId || 'unknown'}`,
              {
                requestedAlias: aliasEncoded,
                receivedAlias: conversationListingAlias,
                conversationId: conv?.conversationId,
              },
            );
          }
          return false;
        });

        if (!isEmpty(matchingConversations)) {
          // Conversations from backend API are an array sorted in reverse chronological order.
          const [mostRecentMessage] = matchingConversations;
          const conversation = Conversation.create(mostRecentMessage, aliasEncoded);

          return dispatch({
            type: constants.UPDATE_LISTING_CONVERSATION,
            payload: {
              aliasEncoded,
              conversation,
            },
          });
        }
      }
    };
  },
};

export default ConversationActions;
